import type {CmLogger} from "@cm/log"
import {Injector} from "@angular/core"
import {Currency, PriceGraphState} from "@generated"
import {deserializePriceGraph, FlattenPriceList, PricingContext} from "@cm/pricing"
import type {PriceEntry, PriceMap} from "@cm/pricing/types/pricing-types"
import {fetchThrowingErrors} from "@common/helpers/api/fetch"
import {PricingHelperGetPriceGraphsGQL, PricingHelperGetPricesGQL} from "@pricing/helpers/pricing.helper.generated"

export interface PricingData {
    rootNode: FlattenPriceList
    priceMap: PriceMap
    availableCurrencies: Currency[]
}

export async function getPriceMap(injector: Injector, organizationId: string, originalIds: string[]): Promise<PriceMap> {
    const numPrices = 2000 //overrides the standard value of 25 and should be sufficient for all cases. Since price graphs are verified on save, this is ok for now.
    const pricesGql = injector.get(PricingHelperGetPricesGQL)
    const {prices} = await fetchThrowingErrors(pricesGql)({
        filter: {originalId: {in: originalIds}, organizationId: {equals: organizationId}},
        take: numPrices,
    })

    const priceMap: PriceMap = new Map()

    for (const price of prices) {
        if (price === null) continue //the null comes from implementFindManyQuery, should not happen here though.
        if (!price.idFromCatalog.originalId) throw new Error("Price object is missing originalId")

        let pricesForItem = priceMap.get(price.idFromCatalog.originalId)
        if (pricesForItem === undefined) {
            pricesForItem = new Map<Currency, PriceEntry>()
            priceMap.set(price.idFromCatalog.originalId, pricesForItem)
        }
        if (pricesForItem.has(price.currency)) {
            throw new Error(`Duplicate price for ${price.idFromCatalog.originalId} in ${price.currency}`)
        }
        pricesForItem.set(price.currency, {price: price.price, priceIncludesVat: price.priceIncludesVat})
    }

    if (priceMap.size < originalIds.length) pricingHelpersLogger?.warn("Not all prices found")

    return priceMap
}

export async function loadPriceGraph(injector: Injector, templateId: string, state: PriceGraphState): Promise<FlattenPriceList | null> {
    const priceGraphsGql = injector.get(PricingHelperGetPriceGraphsGQL)
    const {priceGraphs} = await fetchThrowingErrors(priceGraphsGql)({filter: {templateId: {equals: templateId}, state: state}})

    if (priceGraphs.length > 1) throw new Error("More than one price graph found, revisions not implemented yet")
    if (priceGraphs.length === 0) return null

    return deserializePriceGraph(priceGraphs[0]!.graph)
}

export async function loadPricingData(injector: Injector, organizationId: string, templateId: string, state: PriceGraphState): Promise<PricingData | null> {
    const priceGraph = await loadPriceGraph(injector, templateId, state)
    if (!priceGraph) return null

    const originalIds = priceGraph.getOriginalIdsFromCatalog()
    const priceMap = await getPriceMap(injector, organizationId, originalIds)

    return {
        rootNode: priceGraph,
        priceMap,
        availableCurrencies: PricingContext.getAvailableCurrencies(priceMap),
    }
}

export let pricingHelpersLogger: CmLogger | undefined
